import React, { useContext, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import Table from '@components/common/Table';
import { isAppModule } from '@utils/constants';
import { thresholdContentColumnSelector } from '@store/noc/threshold/selectors';
import { tid } from '@utils/test';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import ParentContext from 'pages/Noc/Threshold/context';
import {
  mainBaseLineColumnV2,
  mainContentColumnV2,
  getThresholdBaselineColumns,
} from '../utils/thresholdColumnsV2';
import Cell from './Cell';
import MetricHeader from './MetricHeader';
import { TableWrapper, BaselineTableWrapper, LoadingWrapper } from './styled';
import { lowercaseSearchStrAtom } from './atoms';
import { useThresholdData } from './hooks';
import { getMetricDisplayName } from '@utils/metrics';
import { getPercentileMetrics } from '@src/utils/metrics/metricHelpers';

interface IProps {
  getThresholdData: () => void;
  setAllThresholdCellSelection: (val: string) => void;
  allCellSelection: string;
}

const ThresholdContainer = ({
  getThresholdData,
  setAllThresholdCellSelection,
  allCellSelection,
}: IProps) => {
  const { thresholdConfig, setThresholdConfig } = useContext(ParentContext);
  const thresholdContentColumn = useSelector(thresholdContentColumnSelector);
  const errorData = useAtomValue(errorDataAtom);
  const [searchStr] = useAtom(lowercaseSearchStrAtom);
  const { loading, errorCode, contentData, baseLineData } = useThresholdData(getThresholdData);

  // select all conviva / all custome
  useEffect(() => {
    if (allCellSelection) {
      const data: { custom?: number; isCustomSelected: boolean } = {
        isCustomSelected: allCellSelection === 'custom',
      };
      const contentConfigs = contentData.reduce((acc, row) => {
        Object.entries(row as Record<string, any>).forEach(([key, value]) => {
          if (!['RowKeys', 'id'].includes(key)) {
            const custom = thresholdConfig.thresholds[value.identifier]?.[key]?.custom;
            const config = custom ? { ...data, custom } : data;
            const identifier = value.identifier;
            acc[identifier] = acc[identifier]
              ? { ...acc[identifier], [key]: config }
              : { [key]: config };
          }
        });
        return acc;
      }, {});

      const baseLineConfigs = baseLineData.reduce((acc, row) => {
        Object.entries(row as Record<string, any>).forEach(([key, value]) => {
          if (!['BaseLine', 'id'].includes(key)) {
            const metrics = value.metric.metrics ? value.metric.metrics : [value.metric.key];
            metrics.forEach((identifier) => {
              const custom = thresholdConfig.threshold_baseline[identifier]?.custom;
              acc[identifier] = custom ? { ...data, custom } : data;
            });
          }
        });
        return acc;
      }, {});

      setThresholdConfig({
        threshold_baseline: baseLineConfigs,
        thresholds: contentConfigs,
      });
      setAllThresholdCellSelection('');
    }
  }, [
    allCellSelection,
    contentData,
    baseLineData,
    setAllThresholdCellSelection,
    thresholdConfig,
    setThresholdConfig,
  ]);

  // base line column config
  const baseLineColumns = useMemo(() => {
    const baseLine = getThresholdBaselineColumns();
    return baseLine.cols.map((column) => ({
      title: column.title,
      dataKey: column.key,
      key: column.key,
      width: 350,
      cellRenderer: ({ cellData }) => (
        <Cell
          cellData={cellData}
          type="threshold_baseline"
          labelAttributes={column.labelAttributes}
        />
      ),
      align: 'left',
      style: { fontSize: '18px', position: 'relative' },
    }));
  }, []);

  // content column config
  const contentColumns = useMemo(() => {
    return thresholdContentColumn
      .filter((column) => !errorData[column.key])
      .map((column) => {
        let title = getMetricDisplayName({ metricKey: column.key });
        if (getPercentileMetrics().includes(column.key) && isAppModule) {
          title += ` (Avg)`;
        }
        return {
          title,
          dataKey: column.key,
          key: column.key,
          width: 280,
          cellRenderer: ({ cellData }) => (
            <Cell type="thresholds" checkForCustom={true} cellData={cellData} />
          ),
          align: 'left',
          style: { fontSize: '18px' },
          headerRenderer: () => <MetricHeader title={title} dataKey={column.key} />,
        };
      });
  }, [thresholdContentColumn, errorData]);

  const baseLineColumnRenderer = [...mainBaseLineColumnV2, ...baseLineColumns];
  const contentColumnRenderer = [...mainContentColumnV2, ...contentColumns];

  const currentContentData = useMemo(() => {
    return contentData.filter((row) => row.RowKeys.toLowerCase().includes(searchStr));
  }, [contentData, searchStr]);

  return (
    <LoadingWrapper isLoading={loading} showError={errorCode !== 0} fetchData={getThresholdData}>
      <BaselineTableWrapper {...tid('threshold-baseline')}>
        <Table
          fixed
          columns={baseLineColumnRenderer}
          data={baseLineData}
          rowHeight={100}
          headerHeight={0}
        />
      </BaselineTableWrapper>
      <TableWrapper {...tid('threshold-rows')}>
        <Table fixed columns={contentColumnRenderer} data={currentContentData} rowHeight={85} />
      </TableWrapper>
    </LoadingWrapper>
  );
};

export default ThresholdContainer;
